/* ═══════════════════════════════════════════════════
   Build-Wave constants
   Extracted from BuildWavePage.js to reduce complexity
   ═══════════════════════════════════════════════════ */
import { Play, Package, Image, TrendingUp, Award, FileText } from 'lucide-react';
import { applyKpiBenchmarkProfile } from './calibrationProfiles';

export const T = '#00F5FF';
export const P = '#FF6BBD';
export const BG = '#0A0A0A';
export const CARD = '#121212';
export const BORDER = '#1E1E1E';

// Fallback SKU list — shown only when user is not a seeded demo brand or
// when the demo-profile fetch hasn't completed yet. Real signups override
// this with their own catalogue via the SKU picker.
export const LUMINA_SKUS = [
  { id: 'sku-001', name: 'Electrolyte Drink Mix – Berry Burst', category: 'Hydration', image: 'https://images.unsplash.com/photo-1543362905-f2423ef4e0f8?w=120&h=80&fit=crop' },
  { id: 'sku-002', name: 'Sleep Recovery Powder', category: 'Sleep', image: 'https://images.unsplash.com/photo-1763757933292-d8290692edde?w=120&h=80&fit=crop' },
  { id: 'sku-003', name: 'Morning Energy Gummies', category: 'Energy', image: 'https://images.unsplash.com/photo-1483918793747-5adbf82956c4?w=120&h=80&fit=crop' },
  { id: 'sku-004', name: 'Tropical Electrolyte Hydration', category: 'Hydration', image: 'https://images.unsplash.com/photo-1762631178325-2c3aea88f166?w=120&h=80&fit=crop' },
  { id: 'sku-005', name: 'Daily Wellness Drops', category: 'Recovery', image: 'https://images.unsplash.com/photo-1625865019554-220ea80ea813?w=120&h=80&fit=crop' },
  { id: 'sku-006', name: 'Post-Workout Recovery Blend', category: 'Recovery', image: 'https://images.unsplash.com/photo-1763757933154-d55844eae856?w=120&h=80&fit=crop' },
];

// A2 (2026-06): LinkedIn removed from selectable platforms — zero SSP
// creator modeling, no catalog entries, no OAuth lane. Invariant: no RFP
// may fan out targeting a platform with zero modeled creators. It renders
// as a disabled "coming soon" chip until creator modeling exists.
export const PLATFORM_OPTIONS = ['Instagram', 'TikTok', 'YouTube'];
export const COMING_SOON_PLATFORMS = ['LinkedIn'];

// R3 amendment (2026-06): inline disclosure for settable targets on
// metrics with no real attribution pipeline yet (A5 finding: ShopMy /
// TikTok Shop attribution NOT integrated; ROAS/GMV/revenue proxy-derived).
// Backend twin: routes/kpi_recommendations.py ATTRIBUTION_DISCLOSURE —
// keep in lockstep (B4 honesty-map pattern: one truth, every surface
// repeats it verbatim).
export const ATTRIBUTION_DISCLOSURE =
  'Attribution not integrated — this value is proxy-derived until ShopMy / TikTok Shop attribution lands. Treat targets as directional.';
export const UNMEASURED_KPI_KEYS = ['roas', 'gmv'];
export const UNMEASURED_PERF_KPI_NAMES = ['GMV Target', 'ROAS', 'ShopMy-Attributed Revenue', 'ShopMy Revenue'];

export const GEO_LEVELS = ['National', 'State', 'DMA', 'Zip Code'];
export const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];
export const TOP_DMAS = ['New York, NY','Los Angeles, CA','Chicago, IL','Philadelphia, PA','Dallas-Fort Worth, TX','Houston, TX','Washington, DC','Atlanta, GA','Boston, MA','San Francisco-Oakland, CA','Phoenix, AZ','Seattle-Tacoma, WA','Tampa-St. Petersburg, FL','Minneapolis-St. Paul, MN','Denver, CO','Miami-Fort Lauderdale, FL','Orlando-Daytona Beach, FL','Sacramento, CA','Portland, OR','Charlotte, NC','Nashville, TN','San Diego, CA','Austin, TX','Columbus, OH','Kansas City, MO'];

export const BASE_PERF_KPIS = {
  TikTok: [
    { name: 'GMV Target', benchmark: '$50K–$200K', required: true, help: 'Total Gross Merchandise Value driven through TikTok Shop' },
    { name: 'Orders', benchmark: '500–5,000', required: true, help: 'Number of completed TikTok Shop purchases' },
    { name: 'ROAS', benchmark: '4x–8x', required: true, help: 'Return on Ad Spend — revenue divided by spend' },
    { name: 'Video Completion Rate', benchmark: '45%–65%', required: false, help: 'Percentage of viewers who watch the full video' },
    { name: 'ShopMy-Attributed Revenue', benchmark: 'Varies', required: false, help: 'Revenue tracked directly through ShopMy affiliate links' },
    { name: 'TikTok Share Rate', benchmark: '2%–5%', required: false, help: 'Percentage of viewers who share the content' },
  ],
  Instagram: [
    { name: 'Link Clicks', benchmark: '5K–50K', required: true, help: 'Total clicks on link in bio or story swipe-ups' },
    { name: 'Add-to-Cart', benchmark: '500–5,000', required: false, help: 'Products added to cart via ShopMy attribution' },
    { name: 'ShopMy Revenue', benchmark: '$10K–$100K', required: true, help: 'Revenue tracked through ShopMy affiliate links' },
    { name: 'Saves', benchmark: '2K–20K', required: false, help: 'Number of times content was saved — strong purchase intent signal' },
    { name: 'Profile Visits', benchmark: '10K–100K', required: false, help: 'Visits to the brand profile driven by creator content' },
  ],
  YouTube: [
    { name: 'Watch Time (hrs)', benchmark: '1K–10K hrs', required: true, help: 'Total hours watched across all creator videos' },
    { name: 'Click-Through Rate', benchmark: '4%–10%', required: true, help: 'Percentage of viewers who click the description link' },
    { name: 'Subscribe Rate', benchmark: '0.5%–2%', required: false, help: 'New subscribers gained from creator content' },
    { name: 'ShopMy-Attributed Revenue', benchmark: 'Varies', required: false, help: 'Revenue tracked via YouTube description ShopMy links' },
  ],
};

// B1 calibration (2026-06): benchmark strings swap per profile.
// Enterprise (default) returns BASE_PERF_KPIS unchanged.
export const PERF_KPIS = applyKpiBenchmarkProfile(BASE_PERF_KPIS);

export const BRAND_KPIS = {
  TikTok: {
    core: ['Reach', 'Impressions / Views', 'Frequency'],
    specific: ['Profile Visits', 'Follower Growth', 'Watch Time', 'Video Completion Rate', 'Duet / Stitch Rate'],
  },
  Instagram: {
    core: ['Reach', 'Impressions', 'Frequency'],
    specific: ['Estimated Ad Recall Lift', 'Profile Visits', 'Follower Growth', 'Saves', 'Story Completion Rate'],
  },
  YouTube: {
    core: ['Impressions', 'Views', 'Unique Reach'],
    specific: ['Average View Duration', 'Impressions CTR', 'Subscribe Rate', 'Playlist Adds'],
  },
};

export const PERF_DELIVERABLES = [
  { title: '30-sec ShopMy Product Demo Reel', desc: 'Short-form demo with direct ShopMy link in bio', icon: Play },
  { title: 'Unboxing + Link in Bio', desc: 'First-impression unboxing with purchase link callout', icon: Package },
  { title: 'Day-in-Life Usage Story', desc: 'Authentic integration into creator daily routine', icon: Image },
  { title: 'Before/After Transformation', desc: 'Visual results showcase with product attribution', icon: TrendingUp },
  { title: 'Review + Purchase Proof', desc: 'Honest review with ShopMy purchase confirmation', icon: Award },
  { title: 'Tutorial / How-To', desc: 'Educational content showing product usage', icon: FileText },
];

export const CREATIVE_ANGLES = [
  { title: 'Authentic Lifestyle Integration', desc: 'Product woven naturally into creator\'s real routine', color: T },
  { title: 'Educational / How-To Storytelling', desc: 'Teach audience something valuable while featuring product', color: P },
  { title: 'Community Challenge / UGC Movement', desc: 'Spark a viral challenge that audiences participate in', color: T },
  { title: 'Before/After Transformation', desc: 'Visual proof of product impact over time', color: P },
  { title: 'Product-in-Real-Life Unboxing', desc: 'Raw, authentic first-impression content', color: T },
  { title: 'Testimonial / Expert Endorsement', desc: 'Credible third-party validation of product claims', color: P },
];

export const STEP_LABELS = ['Overview', 'Cohort', 'Review & Send'];

/**
 * Formats geo-targeting descriptor for summary display.
 * Replaces a nested ternary with an explicit switch.
 */
export function formatGeoTarget(geoLevel, selectedStates, selectedDmas, zipCodes) {
  switch (geoLevel) {
    case 'National':
      return 'National (All US)';
    case 'State':
      return selectedStates.length > 0 ? `${selectedStates.length} states` : '—';
    case 'DMA':
      return selectedDmas.length > 0 ? `${selectedDmas.length} DMAs` : '—';
    case 'Zip Code': {
      const count = zipCodes.split(/[,\n]/).filter(z => z.trim()).length;
      return count > 0 ? `${count} zips` : '—';
    }
    default:
      return '—';
  }
}
